import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Lock, User, AlertCircle, ArrowLeft, Shield, TrendingUp, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import BrandLogo from './BrandLogo'

export default function BrokerLogin() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { login, loginWithToken } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sessionExpired = searchParams.get('expired') === 'true'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    // Demo login fallback (dev only) - use when API is unreachable
    const isDemo = import.meta.env.DEV && username === 'demo' && password === 'demo123'
    if (isDemo) {
      login('demo', 'broker')
      navigate('/broker')
      setLoading(false)
      return
    }

    try {
      const apiUrl = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
        ? ''
        : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000')
      const response = await fetch(`${apiUrl}/api/auth/broker/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        if (data.access_token && data.user) {
          loginWithToken(data.access_token, data.user, data.refresh_token)
        } else {
          login(username, 'broker')
        }
        navigate('/broker')
      } else {
        setError(data.detail || 'Invalid credentials')
      }
    } catch (err) {
      setError('Login failed. Use demo/demo123 for local testing.')
      console.error('Login error:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 flex">
      {/* Left panel — branding & features */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 text-white relative overflow-hidden">
        {/* Decorative background circles */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/5 rounded-full" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-white/5 rounded-full" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-white/5 rounded-full" />

        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center text-white/70 hover:text-white transition-colors mb-8 text-sm">
            <ArrowLeft size={16} className="mr-1.5" />
            Back to Home
          </Link>
          <div className="mb-2">
            <img src="/LoanLoopLogo.jpg" alt="LoanLoop" className="h-14 w-auto rounded-lg shadow-lg mb-4" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Broker Portal</h1>
          <p className="text-lg text-white/70 max-w-md">
            Manage your pipeline, track applications, and close loans faster with LoanLoop.
          </p>
        </div>

        <div className="relative z-10 space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
              <TrendingUp size={20} />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Pipeline Visibility</h3>
              <p className="text-sm text-white/60">Track every application from lead to settlement on your kanban board.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
              <Users size={20} />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Client Management</h3>
              <p className="text-sm text-white/60">Keep all client details, documents, and communications in one place.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
              <Shield size={20} />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Secure & Compliant</h3>
              <p className="text-sm text-white/60">Bank-grade security with full audit trails for regulatory compliance.</p>
            </div>
          </div>
        </div>

        <p className="relative z-10 text-xs text-white/40">
          &copy; {new Date().getFullYear()} LoanLoop Pty Ltd (ACN 696 406 112). All rights reserved.
        </p>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md">
          {/* Mobile back link */}
          <Link to="/" className="lg:hidden inline-flex items-center text-white/70 hover:text-white transition-colors mb-8 text-sm">
            <ArrowLeft size={16} className="mr-1.5" />
            Back to Home
          </Link>

          <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10">
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-6">
              <BrandLogo showTagline={false} linkToHome={false} size="lg" layout="stacked" />
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
              <p className="text-gray-500 mt-1">Sign in to your broker account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Username
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors text-sm"
                    placeholder="Enter your username"
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors text-sm"
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                  />
                </div>
              </div>

              {sessionExpired && (
                <div className="flex items-center gap-2 text-amber-800 bg-amber-50 p-3 rounded-xl border border-amber-200">
                  <AlertCircle size={18} className="flex-shrink-0" />
                  <span className="text-sm">Your session has expired. Please sign in again.</span>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-xl border border-red-100">
                  <AlertCircle size={18} className="flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-600 text-white py-3 rounded-xl hover:bg-primary-700 active:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm shadow-lg shadow-primary-600/25"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">or</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            <Link
              to="/login"
              className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white py-3 rounded-xl hover:bg-gray-800 active:bg-black transition-colors font-medium text-sm"
            >
              Continue with Single Sign-On
            </Link>

            <p className="text-center text-xs text-gray-400 mt-6">
              Protected by enterprise-grade encryption
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
